import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { Button } from '@deliveryhero/pd-cookbook/components/ButtonV2';
import IcBuilding from '@deliveryhero/pd-cookbook/components/Icons/IcBuilding';
import IcChevronDown from '@deliveryhero/pd-cookbook/components/Icons/IcChevronDownSm';
import IcChevronUp from '@deliveryhero/pd-cookbook/components/Icons/IcChevronUpSm';
import IcHelp from '@deliveryhero/pd-cookbook/components/Icons/IcHelp';
import IcProfile from '@deliveryhero/pd-cookbook/components/Icons/IcProfile';
import { Tag, TagVariant } from '@deliveryhero/pd-cookbook/components/Tag';
import { Tooltip } from '@deliveryhero/pd-cookbook/components/Tooltip';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';
import {
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  AppBar as MuiAppBar,
  Toolbar
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import { useAuth } from 'contexts/authentication';
import { AppRoute } from 'share/types/routes';

import { AppBarProps, Props } from './types';

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => !['open', 'width'].some((x) => prop === x)
})<AppBarProps>(({ theme, open, width }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  boxShadow: 'none',
  borderBottom: '1px solid #DCDCDC',
  ...(open && {
    width: `calc(100% - ${width}px)`,
    marginLeft: `${width}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    })
  })
}));

export default function HeaderBar({ width, user }: Props) {
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuWidth, setMenuWidth] = useState<number>(200);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const showTooltip = () => setIsTooltipOpen(true);
  const hideTooltip = () => setIsTooltipOpen(false);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuWidth(rect.width);
    }
  }, [buttonRef]);

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = () => {
    auth.signOut && auth.signOut();
    queryClient.removeQueries();
    navigate(AppRoute.Login, { replace: true });
  };
  const theme = useTheme();
  const iconColor = { fill: theme.palette.neutral.primary };
  return (
    <AppBar
      width={width}
      position="fixed"
      open={true}
      sx={{ background: theme.palette.neutral.surface }}
    >
      <Toolbar
        sx={{ px: theme.spacing(3), justifyContent: 'space-between' }}
        disableGutters
      >
        <div style={{ display: 'flex', gap: '12px' }}>
          <IcBuilding style={iconColor} />
          <Typography type="label-md" color="neutral-primary">
            {user.company.name}
          </Typography>
          {user.company.is_company_pending_gift_card_verification && (
            <div
              data-testid="pending-verification-tooltip"
              onMouseEnter={showTooltip}
              onMouseLeave={hideTooltip}
            >
              <Tooltip
                isVisibleForAnimation={isTooltipOpen}
                arrowAlignment="left"
                bodyText="Once verified, your company can send gift cards"
                placement="bottom"
              >
                <Tag
                  id={TagVariant.ERROR}
                  variant={TagVariant.ERROR}
                  label="Verification pending"
                  icon={<IcHelp width={16} height={16} fill="error" />}
                />
              </Tooltip>
            </div>
          )}
        </div>
        <Button
          prefix={<IcProfile />}
          variant="text"
          suffix={open ? <IcChevronUp /> : <IcChevronDown />}
          onClick={handleProfileClick}
          ref={buttonRef}
        >
          {user.first_name} {user.last_name}
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleProfileClose}
          PaperProps={{
            sx: { width: menuWidth, marginTop: 1, mt: 1 },
            elevation: 0
          }}
        >
          <ListItem divider>
            <ListItemText
              primary={user.first_name + ', ' + user.last_name}
              secondary={user.email}
            />
          </ListItem>
          <MenuItem onClick={handleSignOut}>
            <ListItemText primary="Sign out" />
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
